import type { StateMachine } from './machine';

/** Job state machine — core-data-model §3.1. Job status is explicit stored state. */
export const JOB_STATES = [
  'unscheduled',
  'scheduled',
  'in_progress',
  'work_done',
  'invoiced',
  'closed',
  'canceled',
  'on_hold',
] as const;
export type JobState = (typeof JOB_STATES)[number];

export const jobMachine: StateMachine<JobState> = {
  states: JOB_STATES,
  initial: 'unscheduled',
  terminal: ['closed', 'canceled'],
  transitions: [
    { from: 'unscheduled', to: 'scheduled', via: 'job.schedule' },
    { from: 'scheduled', to: 'unscheduled', via: 'job.unschedule' }, // all visits removed
    { from: 'scheduled', to: 'in_progress', via: 'job.start', system: true }, // first visit starts
    { from: 'in_progress', to: 'work_done', via: 'job.completeWork' },
    { from: 'work_done', to: 'invoiced', via: 'invoice.issue' }, // M2
    { from: 'work_done', to: 'closed', via: 'job.close' },
    { from: 'invoiced', to: 'closed', via: 'job.close', system: true },
    // A void that leaves something to bill (Inventory v2 ticket 12a): the job goes back to
    // being billed, and takes parts again, rather than reading "closed" for good.
    { from: 'invoiced', to: 'work_done', via: 'invoice.void', system: true },
    // Ruled 2026-10-07 (12a.1): voiding a CLOSED job's invoice reopens it the same way, to be
    // billed again. The one way out of a terminal state, and only the system takes it.
    { from: 'closed', to: 'work_done', via: 'invoice.void', system: true },
    // Ruled 2026-10-07 (12a.1): a put-back that leaves nothing to bill on a finished job whose
    // invoice is issued bills it out (P6), as issuing or completing the work does.
    { from: 'work_done', to: 'invoiced', via: 'job.billedOut', system: true },
    { from: 'unscheduled', to: 'canceled', via: 'job.cancel' },
    { from: 'scheduled', to: 'canceled', via: 'job.cancel' },
    { from: 'scheduled', to: 'on_hold', via: 'job.hold' },
    { from: 'in_progress', to: 'on_hold', via: 'job.hold' },
    { from: 'on_hold', to: 'scheduled', via: 'job.resume' },
    // NOTE: in_progress → canceled is deliberately ILLEGAL — cancel visits first, forcing
    // an explicit decision about work already done (core-data-model §3.1).
  ],
};

/**
 * The job states that take no more parts: billed out, closed, canceled. Derived from the machine
 * plus `invoiced`, as `spine-internal.ts` derives its settled set, so a new terminal state joins
 * by itself.
 */
const CLOSED_TO_PARTS: ReadonlySet<string> = new Set<string>([
  ...jobMachine.terminal,
  'invoiced',
]);

/**
 * Why this job takes no more parts, or null when it still does (Inventory v2 amendment
 * 2026-10-06, P1). Whatever the job's pricing basis: parts sold after a job is billed out go on a
 * new job, linked to it. The server refuses a use with exactly this sentence; the screens show it
 * in place of "Add a part", so the two can never disagree.
 */
export function partsClosedReason(job: {
  status: string;
  jobNumber: number;
}): string | null {
  if (!CLOSED_TO_PARTS.has(job.status)) return null;
  const why = job.status === 'canceled' ? 'canceled' : 'closed';
  return `Job #${job.jobNumber} is ${why}. Use these parts on a new job.`;
}

/**
 * Why a T&M job takes no more time, or null when it still does (Inventory v2 ticket 13, P1 as
 * for parts): its time is what it bills, so hours added after it is billed out would bill nothing.
 * A Fixed job's time is its cost only, so a late timesheet still goes on it (the caller asks this
 * only of a T&M job).
 */
export function timeClosedReason(job: {
  status: string;
  jobNumber: number;
}): string | null {
  if (!CLOSED_TO_PARTS.has(job.status)) return null;
  const why = job.status === 'canceled' ? 'canceled' : 'closed';
  return `Job #${job.jobNumber} is ${why}. Log this time on a new job.`;
}
